import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, formatDateTime } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Building2, Users, Mail, Send, X, ShieldCheck, Shield, Trash2, BarChart3, Tags } from "lucide-react";

export default function OrganisationSettings() {
  const { user: currentUser, refreshUser } = useAuth();
  const [org, setOrg] = useState(null);
  const [stats, setStats] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgName, setOrgName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [addingCat, setAddingCat] = useState(false);

  const addCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;
    setAddingCat(true);
    try {
      const { data } = await api.post("/categories", { name: newCategory.trim() });
      setCategories((list) => [...list, data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewCategory("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAddingCat(false);
    }
  };

  const removeCategory = async (id) => {
    try {
      await api.delete(`/categories/${id}`);
      setCategories((list) => list.filter((c) => c.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/organisation/digest");
      const n = data.recipients?.length || 0;
      toast.success(n ? `Digest emailed to ${n} admin${n === 1 ? "" : "s"}` : "No admins to email");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingDigest(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const [o, i, s, c] = await Promise.all([api.get("/organisation"), api.get("/invites"), api.get("/organisation/stats"), api.get("/categories")]);
      setOrg(o.data);
      setOrgName(o.data.name);
      setInvites(i.data);
      setStats(s.data);
      setCategories(c.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveName = async (e) => {
    e.preventDefault();
    if (!orgName.trim()) return;
    setSavingName(true);
    try {
      await api.patch("/organisation", { name: orgName.trim() });
      toast.success("Organisation name updated");
      await refreshUser();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingName(false);
    }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const { data } = await api.post("/invites", { email: inviteEmail.trim().toLowerCase() });
      toast.success(`Invitation sent to ${data.email}`);
      setInviteEmail("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setInviting(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/invites/${id}`);
      toast.success("Invite revoked");
      setInvites((list) => list.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const changeRole = async (m) => {
    const next = m.role === "admin" ? "submitter" : "admin";
    try {
      await api.patch(`/members/${m.id}`, { role: next });
      toast.success(`${m.name || m.email} is now ${next === "admin" ? "an admin" : "a submitter"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name || m.email} from the organisation? Their submitted expenses stay in the org.`)) return;
    try {
      await api.delete(`/members/${m.id}`);
      toast.success(`${m.name || m.email} removed`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Layout>
      <div className="mb-8 animate-fade-up">
        <p className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-2">Organisation</p>
        <h1 className="font-heading text-3xl sm:text-4xl text-forest-900 tracking-tight">Settings</h1>
        <p className="text-sm text-ink-soft mt-2 max-w-lg">
          Manage your charity's workspace, invite team members, and review pending invitations.
        </p>
      </div>

      {loading ? (
        <div className="p-12 flex items-center justify-center text-ink-soft">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <>
        {stats && (
          <section className="mb-6" data-testid="org-activity">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-forest-700" />
                <h2 className="font-heading text-lg text-forest-900">Activity</h2>
              </div>
              <Button
                size="sm" variant="outline"
                onClick={sendDigest}
                disabled={sendingDigest}
                data-testid="send-digest-button"
                className="border-cream-200 hover:bg-cream-100 text-forest-800"
              >
                {sendingDigest ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Mail className="h-3.5 w-3.5 mr-1.5" /> Email digest to admins</>}
              </Button>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="Awaiting" value={stats.awaiting} accent="amber" testid="stat-awaiting" />
              <StatCard label="Approved" value={stats.approved} accent="emerald" testid="stat-approved" />
              <StatCard label="Rejected" value={stats.rejected} accent="rose" testid="stat-rejected" />
              <StatCard
                label="Avg. time to approve"
                value={!stats.avg_approval_hours ? "—" : formatDuration(stats.avg_approval_hours)}
                testid="stat-avg-approval"
              />
            </div>
            <div className="mt-3 text-xs text-ink-soft" data-testid="stat-this-month">
              This month: <strong className="text-forest-800">{stats.this_month.created}</strong> submitted ·{" "}
              <strong className="text-emerald-700">{stats.this_month.approved}</strong> approved ·{" "}
              <strong className="text-rose-700">{stats.this_month.rejected}</strong> rejected
            </div>
          </section>
        )}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Org name */}
          <section className="bg-white border border-cream-200 rounded-xl p-6" data-testid="org-name-card">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="h-4 w-4 text-forest-700" />
              <h2 className="font-heading text-lg text-forest-900">Organisation name</h2>
            </div>
            <form onSubmit={saveName} className="space-y-3">
              <Label htmlFor="orgName" className="text-xs font-semibold uppercase tracking-wider text-ink-soft">Name</Label>
              <Input id="orgName" value={orgName} onChange={(e) => setOrgName(e.target.value)} data-testid="org-name-input"
                className="bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700" />
              <Button type="submit" disabled={savingName} data-testid="org-name-save-button"
                className="bg-forest-700 hover:bg-forest-800 text-cream-50">
                {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save name"}
              </Button>
            </form>
          </section>

          {/* Expense categories */}
          <section className="bg-white border border-cream-200 rounded-xl p-6" data-testid="categories-card">
            <div className="flex items-center gap-2 mb-4">
              <Tags className="h-4 w-4 text-forest-700" />
              <h2 className="font-heading text-lg text-forest-900">Expense categories</h2>
            </div>
            <p className="text-sm text-ink-soft mb-3">
              Categories (e.g. budget codes or grant names) submitters can tag expenses with. They appear on the CSV export and finance email.
            </p>
            <form onSubmit={addCategory} className="flex gap-2 mb-4">
              <Input
                value={newCategory} onChange={(e) => setNewCategory(e.target.value)}
                placeholder="e.g. Grant A – Youth" maxLength={60} data-testid="category-input"
                className="bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700"
              />
              <Button type="submit" disabled={addingCat} data-testid="add-category-button"
                className="bg-forest-700 hover:bg-forest-800 text-cream-50 shrink-0">
                {addingCat ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
              </Button>
            </form>
            {categories.length === 0 ? (
              <p className="text-sm text-ink-mute">No categories yet.</p>
            ) : (
              <ul className="flex flex-wrap gap-2" data-testid="categories-list">
                {categories.map((c) => (
                  <li key={c.id} data-testid={`category-${c.id}`}
                    className="inline-flex items-center gap-1.5 bg-forest-50 border border-forest-100 rounded-full pl-3 pr-1.5 py-1">
                    <span className="text-sm text-forest-800">{c.name}</span>
                    <button
                      type="button" onClick={() => removeCategory(c.id)}
                      data-testid={`delete-category-${c.id}`} aria-label={`Delete ${c.name}`}
                      className="h-5 w-5 grid place-items-center rounded-full text-ink-mute hover:text-rose-700 hover:bg-rose-50"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Invite */}
          <section className="bg-white border border-cream-200 rounded-xl p-6" data-testid="invite-card">
            <div className="flex items-center gap-2 mb-4">
              <Send className="h-4 w-4 text-forest-700" />
              <h2 className="font-heading text-lg text-forest-900">Invite a team member</h2>
            </div>
            <p className="text-sm text-ink-soft mb-3">They'll join as a <strong>submitter</strong> and can create expenses.</p>
            <form onSubmit={sendInvite} className="flex gap-2">
              <Input type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@charity.org" data-testid="invite-email-input"
                className="bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700" />
              <Button type="submit" disabled={inviting} data-testid="send-invite-button"
                className="bg-forest-700 hover:bg-forest-800 text-cream-50 shrink-0">
                {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-1.5" />Invite</>}
              </Button>
            </form>
          </section>

          {/* Members */}
          <section className="bg-white border border-cream-200 rounded-xl p-6" data-testid="members-card">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-4 w-4 text-forest-700" />
              <h2 className="font-heading text-lg text-forest-900">Members ({org.members.length})</h2>
            </div>
            <ul className="divide-y divide-cream-200" data-testid="members-list">
              {org.members.map((m) => {
                const isSelf = m.id === currentUser?.id;
                return (
                  <li key={m.id} className="py-3 flex items-center justify-between gap-2" data-testid={`member-${m.email}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-forest-900 truncate">
                        {m.name || m.email}{isSelf && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-ink-mute">(you)</span>}
                      </div>
                      <div className="text-xs text-ink-soft truncate">{m.email}</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold inline-flex items-center gap-1 ${
                        m.role === "admin" ? "bg-forest-50 text-forest-800" : "bg-cream-100 text-ink-soft"
                      }`}>
                        {m.role === "admin" && <ShieldCheck className="h-3 w-3" />}{m.role}
                      </span>
                      {!isSelf && (
                        <>
                          <button
                            type="button"
                            onClick={() => changeRole(m)}
                            data-testid={`toggle-role-${m.email}`}
                            title={m.role === "admin" ? "Demote to submitter" : "Promote to admin"}
                            aria-label={m.role === "admin" ? `Demote ${m.email} to submitter` : `Promote ${m.email} to admin`}
                            className="p-1.5 rounded-md text-ink-soft hover:text-forest-800 hover:bg-cream-100"
                          >
                            <Shield className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeMember(m)}
                            data-testid={`remove-member-${m.email}`}
                            title="Remove member"
                            aria-label={`Remove ${m.email} from organisation`}
                            className="p-1.5 rounded-md text-ink-soft hover:text-rose-700 hover:bg-rose-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>

          {/* Pending invites */}
          <section className="bg-white border border-cream-200 rounded-xl p-6" data-testid="pending-invites-card">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="h-4 w-4 text-forest-700" />
              <h2 className="font-heading text-lg text-forest-900">Pending invites ({invites.length})</h2>
            </div>
            {invites.length === 0 ? (
              <p className="text-sm text-ink-soft py-2">No pending invitations.</p>
            ) : (
              <ul className="divide-y divide-cream-200" data-testid="invites-list">
                {invites.map((inv) => (
                  <li key={inv.id} className="py-3 flex items-center justify-between gap-2" data-testid={`invite-${inv.email}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-forest-900 truncate">{inv.email}</div>
                      <div className="text-xs text-ink-soft">Invited {formatDateTime(inv.created_at)}</div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => revoke(inv.id)} data-testid={`revoke-invite-${inv.email}`}
                      className="border-cream-200 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200 shrink-0">
                      <X className="h-3.5 w-3.5 mr-1" /> Revoke
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
        </>
      )}
    </Layout>
  );
}

function StatCard({ label, value, accent, testid }) {
  const ring = {
    amber: "before:bg-amber-400",
    emerald: "before:bg-emerald-500",
    rose: "before:bg-rose-400",
  }[accent] || "before:bg-forest-700";
  return (
    <div
      data-testid={testid}
      className={`relative bg-white border border-cream-200 rounded-xl p-4 overflow-hidden before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[3px] ${ring}`}
    >
      <div className="text-[10px] uppercase tracking-[0.16em] text-ink-soft font-semibold">{label}</div>
      <div className="font-heading text-3xl text-forest-900 mt-1.5">{value}</div>
    </div>
  );
}

function formatDuration(hours) {
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 48) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}
